import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { WebhookEndpoint } from 'src/features/webhook-endpoints/utils/types';
import { useDeleteWebhookEndpoint } from 'src/features/webhook-endpoints/api/useDeleteWebhookEndpoint';
import { LIST_WEBHOOK_ENDPOINTS_RESOURCE } from 'src/features/webhook-endpoints/api/useListWebhookEndpoints';
import { webhookEndpointsMessages } from 'src/features/webhook-endpoints/utils/messages';

interface DeleteWebhookEndpointFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedWebhookEndpoint: WebhookEndpoint;
    children?: React.ReactNode;
}

function DeleteWebhookEndpointFormModal<TData>({
    selectedWebhookEndpoint,
    children,
}: DeleteWebhookEndpointFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: deleteWebhookEndpoint } = useDeleteWebhookEndpoint({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_WEBHOOK_ENDPOINTS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(webhookEndpointsMessages.deleteWebhookEndpointSuccess),
                description: formatMessage(webhookEndpointsMessages.deleteWebhookEndpointSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...webhookEndpointsMessages.deleteWebhookEndpoint },
                subTitle: {
                    ...webhookEndpointsMessages.deleteWebhookEndpointSubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...webhookEndpointsMessages.confirmDeleteWebhookEndpoint },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: webhookEndpointsMessages.deleteWebhookEndpoint,
                callbackFn: () => {
                    return (
                        deleteWebhookEndpoint({
                            endpointId: selectedWebhookEndpoint.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteWebhookEndpointFormModal;
